import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { api, Ic, useMediaMinWidth } from "../shared";
import { dateLocaleForAppLang } from "../i18n";

function parseAmount(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const s = String(raw).trim().replace(",", ".");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function formatThreshold(lang, currencyCode, rawMin) {
  if (rawMin == null || rawMin === "") return "—";
  const n = parseAmount(rawMin);
  if (n == null) return String(rawMin);
  const loc = dateLocaleForAppLang(lang);
  const code = (currencyCode && String(currencyCode).trim()) || "EUR";
  try {
    return new Intl.NumberFormat(loc, { style: "currency", currency: code }).format(n);
  } catch {
    return `${n} ${code}`;
  }
}

/** jsonb a volte stringa; numeri a volte in DB come stringa. */
function normalizeIdArray(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x).trim()).filter(Boolean);
  }
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t.startsWith("[")) {
      try {
        const j = JSON.parse(t);
        return Array.isArray(j) ? j.map((x) => String(x).trim()).filter(Boolean) : [];
      } catch {
        return [];
      }
    }
    return t.split(/[\s,]+/).map((x) => x.trim()).filter((x) => /^\d+$/.test(x));
  }
  return [];
}

function normalizePoeScopesRaw(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const j = JSON.parse(raw);
      return Array.isArray(j) ? j : [];
    } catch {
      return [];
    }
  }
  return [];
}

function formatSyncedAt(iso, loc) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(loc, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function Row({ label, children }) {
  return (
    <div className="ch-pa-wp-import__row">
      <div className="ch-pa-wp-import__row-label">{label}</div>
      <div className="ch-pa-wp-import__row-value">{children}</div>
    </div>
  );
}

/** Soglia / categorie / prodotti PA + POE sincronizzati da WordPress (sola lettura), layout 2 colonne. */
export default function PriceAlertWpScopeReadonly({ shop }) {
  const { t, i18n } = useTranslation("priceAlertSetup");
  const isDesktop = useMediaMinWidth(900);
  const shopId = shop?.id;
  const [d, setD] = useState(null);
  const [load, setLoad] = useState(true);

  useEffect(() => {
    if (!shopId) {
      setLoad(false);
      return;
    }
    let cancelled = false;
    api(`/settings/${shopId}`)
      .then((body) => {
        if (!cancelled) setD(body && typeof body === "object" ? body : {});
      })
      .catch(() => {
        if (!cancelled) setD({});
      })
      .finally(() => {
        if (!cancelled) setLoad(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shopId]);

  const loc = dateLocaleForAppLang(i18n.language);

  const { fmtAt, fmtAtPoe, showNeverHint } = useMemo(() => {
    const at = d?.price_alert_plugin_scope_synced_at;
    const poeAt = d?.poe_scope_synced_at;
    return {
      fmtAt: formatSyncedAt(at, loc),
      fmtAtPoe: formatSyncedAt(poeAt, loc),
      showNeverHint: !at,
    };
  }, [d?.price_alert_plugin_scope_synced_at, d?.poe_scope_synced_at, loc]);

  if (load) {
    return <div className="ch-pa-wp-import ch-pa-wp-import--loading">{t("wpScope.loading")}</div>;
  }

  if (!shopId) {
    return null;
  }

  const minRaw = d?.price_alert_plugin_min_price;
  const cur = d?.price_alert_plugin_currency;
  const cats = normalizeIdArray(d?.price_alert_plugin_category_ids);
  const prods = normalizeIdArray(d?.price_alert_plugin_product_ids);
  const paEnabled = !!d?.price_alert_plugin_enabled;
  const hasPaRestriction = cats.length > 0 || prods.length > 0;
  const poeActive = !!d?.poe_plugin_enabled;
  const poeDelay = Number.isFinite(Number(d?.poe_delay_hours)) ? Number(d?.poe_delay_hours) : 0;
  const poeOfferType = d?.poe_offer_type === "fixed" ? "fixed" : "percent";
  let poeScopes = normalizePoeScopesRaw(d?.poe_scopes);
  const poeCatLegacy = normalizeIdArray(d?.poe_category_ids);
  const poeProdLegacy = normalizeIdArray(d?.poe_product_ids);
  if (poeScopes.length === 0 && (poeCatLegacy.length > 0 || poeProdLegacy.length > 0)) {
    poeScopes = [
      {
        id: "legacy",
        label: "",
        category_ids: poeCatLegacy,
        product_ids: poeProdLegacy,
        offer_type: poeOfferType,
        offer_percent: d?.poe_offer_percent,
        offer_fixed_amount: d?.poe_offer_fixed_amount,
      },
    ];
  }

  const formatPoeScopeOffer = (sc) => {
    const ot = sc?.offer_type === "fixed" ? "fixed" : "percent";
    if (ot === "fixed") {
      return formatThreshold(i18n.language, cur, sc?.offer_fixed_amount);
    }
    const p = parseAmount(sc?.offer_percent);
    return p != null ? `${p}%` : "—";
  };

  const paScopeBlock = (() => {
    if (hasPaRestriction) {
      return (
        <>
          <p className="ch-pa-wp-import__scope-note">{t("wpScope.activationRestrictedHint")}</p>
          {cats.length > 0 && (
            <div className="ch-pa-wp-import__ids">
              <span className="ch-pa-wp-import__id-label">{t("wpScope.categories")}</span>{" "}
              {cats.join(", ")}
            </div>
          )}
          {prods.length > 0 && (
            <div className="ch-pa-wp-import__ids">
              <span className="ch-pa-wp-import__id-label">{t("wpScope.products")}</span>{" "}
              {prods.join(", ")}
            </div>
          )}
        </>
      );
    }
    if (paEnabled) {
      return <p className="ch-pa-wp-import__scope-note">{t("wpScope.activationGlobal")}</p>;
    }
    return <p className="ch-pa-wp-import__scope-note ch-pa-wp-import__scope-note--muted">{t("wpScope.activationGlobalWhenModuleOff")}</p>;
  })();

  const firstScope = poeScopes[0];
  const firstHasIds =
    poeScopes.length === 1 &&
    ((Array.isArray(firstScope?.category_ids) && firstScope.category_ids.length > 0) ||
      (Array.isArray(firstScope?.product_ids) && firstScope.product_ids.length > 0));
  // Multi-fence: per-recinto offer; single store-wide: offer is in the summary block, not a duplicate "type" row
  const showPoeTypeRow = firstHasIds;

  const poeFencesBlock = (() => {
    if (poeScopes.length === 0) {
      return <p className="ch-pa-wp-import__scope-note ch-pa-wp-import__scope-note--muted">—</p>;
    }
    if (poeScopes.length === 1) {
      const sc = poeScopes[0];
      const cids = Array.isArray(sc?.category_ids) ? sc.category_ids : [];
      const pids = Array.isArray(sc?.product_ids) ? sc.product_ids : [];
      if (cids.length === 0 && pids.length === 0) {
        return (
          <div>
            <p className="ch-pa-wp-import__scope-note">
              {poeActive ? t("wpScope.poeOfferGlobal") : t("wpScope.poeScopeWhenModuleOff")}
            </p>
            <p className="ch-pa-wp-import__poe-offer-line">
              <strong>{formatPoeScopeOffer(sc)}</strong>{" "}
              <span className="ch-pa-wp-import__muted">({poeOfferType === "fixed" ? t("wpScope.fixed") : t("wpScope.percent")})</span>
            </p>
          </div>
        );
      }
    }
    return (
      <ul className="ch-pa-wp-import__poe-list">
        {poeScopes.map((sc, i) => (
          <li key={String(sc?.id ?? i)}>
            <div className="ch-pa-wp-import__poe-fence-name">
              {sc?.label && String(sc.label).trim() !== ""
                ? String(sc.label)
                : t("wpScope.poeFence", { n: i + 1 })}
              <span className="ch-pa-wp-import__poe-offer-inline"> — {formatPoeScopeOffer(sc)}</span>
            </div>
            <div className="ch-pa-wp-import__poe-fence-ids">
              {t("wpScope.poeScopeLine", {
                cats:
                  Array.isArray(sc?.category_ids) && sc.category_ids.length
                    ? sc.category_ids.join(", ")
                    : "—",
                prods:
                  Array.isArray(sc?.product_ids) && sc.product_ids.length
                    ? sc.product_ids.join(", ")
                    : "—",
              })}
            </div>
          </li>
        ))}
      </ul>
    );
  })();

  return (
    <section
      className="ch-pa-wp-import"
      aria-label={t("wpScope.ariaImportBlock")}
    >
      <div className="ch-pa-wp-import__accent" aria-hidden="true" />
      <div className="ch-pa-wp-import__inner">
        <div className="ch-pa-wp-import__head">
          <div className="ch-pa-wp-import__head-icon" aria-hidden="true">
            {Ic.bell}
          </div>
          <div>
            <h2 className="ch-pa-wp-import__title">{t("wpScope.importBlockTitle")}</h2>
            <p className="ch-pa-wp-import__sub">{t("wpScope.importBlockSubtitle")}</p>
          </div>
        </div>

        {showNeverHint && (
          <div className="ch-pa-wp-import__alert">
            {t("wpScope.never")}
          </div>
        )}

        <div
          className="ch-pa-wp-import__split"
          style={{ flexDirection: isDesktop ? "row" : "column" }}
        >
          <div className="ch-pa-wp-import__col ch-pa-wp-import__col--pa">
            <div className="ch-pa-wp-import__col-rail" aria-hidden="true" />
            <div className="ch-pa-wp-import__col-body">
              <h3 className="ch-pa-wp-import__col-title">{t("wpScope.columnPriceAlert")}</h3>
              <Row label={t("wpScope.colModule")}>
                <span
                  className={paEnabled ? "ch-status success" : "ch-status neutral"}
                  style={{ display: "inline-flex" }}
                >
                  <span className="status-dot" />
                  {paEnabled ? t("wpScope.pluginOn") : t("wpScope.pluginOff")}
                </span>
              </Row>
              <Row label={t("wpScope.threshold")}>{formatThreshold(i18n.language, cur, minRaw)}</Row>
              <Row label={t("wpScope.currency")}>{cur || "—"}</Row>
              <div className="ch-pa-wp-import__row">
                <div className="ch-pa-wp-import__row-label">{t("wpScope.activationScope")}</div>
                <div className="ch-pa-wp-import__row-value">{paScopeBlock}</div>
              </div>
            </div>
          </div>

          <div className="ch-pa-wp-import__col ch-pa-wp-import__col--poe">
            <div className="ch-pa-wp-import__col-rail ch-pa-wp-import__col-rail--poe" aria-hidden="true" />
            <div className="ch-pa-wp-import__col-body">
              <h3 className="ch-pa-wp-import__col-title">{t("wpScope.columnPoe")}</h3>
              <Row label={t("wpScope.poeModuleLabel")}>
                <span
                  className={poeActive ? "ch-status success" : "ch-status neutral"}
                  style={{ display: "inline-flex" }}
                >
                  <span className="status-dot" />
                  {poeActive ? t("wpScope.pluginOn") : t("wpScope.pluginOff")}
                </span>
              </Row>
              <Row label={t("wpScope.poeDelay")}>{poeDelay}h</Row>
              {showPoeTypeRow ? (
                <Row label={t("wpScope.poeOfferType")}>
                  {poeOfferType === "fixed" ? t("wpScope.fixed") : t("wpScope.percent")}
                </Row>
              ) : null}
              {poeScopes.length > 1 ? (
                <p className="ch-pa-wp-import__hint">{t("wpScope.poePerFenceOffer")}</p>
              ) : null}
              <div className="ch-pa-wp-import__row">
                <div className="ch-pa-wp-import__row-label">{t("wpScope.poeFencesTitle")}</div>
                <div className="ch-pa-wp-import__row-value">{poeFencesBlock}</div>
              </div>
            </div>
          </div>
        </div>

        {(fmtAt || fmtAtPoe) && (
          <div className="ch-pa-wp-import__foot">
            {fmtAt ? (
              <span>
                {t("wpScope.syncLineWidget")} <strong>{fmtAt}</strong>
              </span>
            ) : null}
            {fmtAtPoe ? (
              <span>
                {fmtAt ? " · " : null}
                {t("wpScope.syncLinePoe")} <strong>{fmtAtPoe}</strong>
              </span>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}
